

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'white' | 'gold';
  className?: string;
  label?: string;
}

export function LoadingSpinner({ 
  size = 'medium', 
  color = 'primary', 
  className = '',
  label = 'Loading...'
}: LoadingSpinnerProps) {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8'
  };

  const colorClasses = {
    primary: 'border-primary-green border-t-transparent',
    secondary: 'border-accent-green border-t-transparent',
    white: 'border-white border-t-transparent',
    gold: 'border-gold border-t-transparent',
    binance: 'border-t-transparent'
  };

  return (
    <div className={`inline-flex items-center ${className}`} role="status" aria-label={label}>
      <div 
        className={`
          animate-spin rounded-full border-2 
          ${sizeClasses[size]} 
          ${colorClasses[color]}
        `}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}

interface LoadingDotsProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'white' | 'gold';
  className?: string;
  label?: string;
}

export function LoadingDots({ 
  size = 'medium', 
  color = 'primary', 
  className = '',
  label = 'Loading...'
}: LoadingDotsProps) {
  const sizeClasses = {
    small: 'w-1 h-1',
    medium: 'w-2 h-2',
    large: 'w-3 h-3'
  };

  const colorClasses = {
    primary: 'bg-primary-green',
    secondary: 'bg-accent-green',
    white: 'bg-white',
    gold: 'bg-gold'
  };

  return (
    <div className={`inline-flex items-center space-x-1 ${className}`} role="status" aria-label={label}>
      <div 
        className={`
          rounded-full animate-pulse
          ${sizeClasses[size]} 
          ${colorClasses[color]}
        `}
        style={{ animationDelay: '0ms' }}
      />
      <div 
        className={`
          rounded-full animate-pulse
          ${sizeClasses[size]} 
          ${colorClasses[color]}
        `}
        style={{ animationDelay: '150ms' }}
      />
      <div 
        className={`
          rounded-full animate-pulse
          ${sizeClasses[size]} 
          ${colorClasses[color]}
        `}
        style={{ animationDelay: '300ms' }}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}

interface LoadingBarProps {
  progress?: number; // 0-100
  color?: 'primary' | 'secondary' | 'gold';
  className?: string;
  label?: string;
  showPercentage?: boolean;
}

export function LoadingBar({ 
  progress, 
  color = 'primary', 
  className = '',
  label = 'Loading...',
  showPercentage = false
}: LoadingBarProps) {
  const colorClasses = {
    primary: 'bg-primary-green',
    secondary: 'bg-accent-green',
    gold: 'bg-gold'
  };

  const isIndeterminate = progress === undefined;

  return (
    <div className={`w-full ${className}`} role="progressbar" aria-label={label} aria-valuenow={progress}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {showPercentage && progress !== undefined && (
          <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`
            h-2 rounded-full transition-all duration-300 ease-out
            ${colorClasses[color]}
            ${isIndeterminate ? 'animate-pulse' : ''}
          `}
          style={{ 
            width: isIndeterminate ? '100%' : `${Math.max(0, Math.min(100, progress || 0))}%` 
          }}
        />
      </div>
    </div>
  );
}